// src/services/orderService.js

import apiClient from '../api/apiClient';
import { getOrgId } from '../utils/orgUtils';

/**
 * Fetch all orders with pagination support.
 * @param {Object} options - Pagination and filtering options.
 * @returns {Promise<Object>} Promise resolving to paginated orders data.
 */
export const getOrders = async (options = {}) => {
  try {
    const orgId = getOrgId();
    const params = new URLSearchParams();
    
    // Add pagination parameters if provided
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    
    const queryString = params.toString();
    const url = `/orders/org/${orgId}${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get(url);
    
    // Check if response is paginated
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      // It's paginated - process the data array
      const processedData = response.data.data.map(processOrderData);
      return {
        ...response.data,
        data: processedData
      };
    } else if (Array.isArray(response.data)) {
      // It's not paginated - process the array directly
      return response.data.map(processOrderData);
    } else {
      // Unexpected format
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

/**
 * Get total order count for current org.
 * @returns {Promise<number>} Total orders
 */
export const getOrderCount = async () => {
  try {
    const orgId = getOrgId();
    const response = await apiClient.get(`/orders/org/${orgId}/count`);
    // supports either { total } or numeric
    return typeof response.data === 'number' ? response.data : (response.data?.total ?? 0);
  } catch (error) {
    console.error('Error fetching order count:', error);
    throw error;
  }
};

/**
 * Add a new order.
 * @param {Object} data - The order data.
 * @returns {Promise<Object>} Promise resolving to the created order data.
 */
export const addOrder = async (data) => {
  try {
    const orgId = getOrgId();
    
    // Format line items to include discount information
    const formattedData = {
      ...data,
      org_id: orgId,
      line_items: data.line_items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
        original_unit_price: item.original_unit_price || item.unit_price,
        discount_type: item.discount_type || 'none',
        discount_value: item.discount_value || null
      })),
      // Ensure meta is included in the formattedData
      meta: {
        ...data.meta
      },
      payment_type: data.payment_type,
      payment_due_date: data.payment_due_date,
      accounts_receivable: data.accounts_receivable,
      accounts_payable: data.accounts_payable
    };
    
    const response = await apiClient.post('/orders', formattedData);
    return response.data;
  } catch (error) {
    console.error('Error adding order:', error);
    throw error;
  }
};

/**
 * Process order data to ensure consistent format
 * @param {Object} order - The order data from API
 * @returns {Object} Processed order with normalized fields
 */
const processOrderData = (order) => {
  if (!order) return null;
  
  // Ensure line items have consistent discount format
  if (order.lineItems) {
    order.lineItems = order.lineItems.map(item => {
      // If discount info is in meta, add it to line item directly
      if (order.meta?.discounts) {
        const discountInfo = order.meta.discounts.find(d => d.product_id === item.product?.id);
        if (discountInfo) {
          item.discount_type = item.discount_type || discountInfo.discount_type;
          item.discount_value = item.discount_value || discountInfo.discount_value;
        }
      }
      
      // If we have discount info but no original price, calculate it
      if ((item.discount_type && item.discount_type !== 'none') && !item.original_unit_price) {
        if (item.discount_type === 'percentage' && item.discount_value) {
          item.original_unit_price = parseFloat(item.unit_price) / (1 - parseFloat(item.discount_value) / 100);
        } else if (item.discount_type === 'amount' && item.discount_value) {
          item.original_unit_price = parseFloat(item.unit_price) + parseFloat(item.discount_value);
        }
      }
      
      return {
        ...item,
        discount_type: item.discount_type || 'none',
        discount_value: item.discount_value || null,
        original_unit_price: item.original_unit_price || item.unit_price
      };
    });
  }
  
  // Just check if deliveryAddress exists in meta, no need to reassign
  // Removing the self-assignment that was causing the warning
  
  return order;
};

/**
 * Find an order by ID.
 * @param {string} orderId - The order ID to search for.
 * @returns {Promise<Object|null>} Promise resolving to the found order or null.
 */
export const getOrderById = async (id) => {
  try {
    const response = await apiClient.get(`/orders/${id}`);
    return processOrderData(response.data);
  } catch (error) {
    throw error;
  }
};

export const updateOrderStatus = async (orderId, status) => {
  const response = await apiClient.patch(`/orders/${orderId}/status`, { status });
  return response.data;
};

export const deleteMultipleOrders = (orderIds) => {
  return apiClient.post('/orders/bulk-delete', { order_ids: orderIds })
    .then(response => response.data);
};

/**
 * Upload an attachment for an order
 * @param {File} file - The file to upload
 * @param {string|number} orgId - Organization ID
 * @returns {Promise<Object>} Promise resolving to the file data (URL, name, type)
 */
export const uploadOrderAttachment = async (file, orgId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('org_id', orgId);
    
    const response = await apiClient.post('/file-upload/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading order attachment:', error);
    throw error;
  }
};

/**
 * Get accounts receivable for a specific customer or all customers
 * @param {number} customerId - Optional customer ID to filter by
 * @returns {Promise<Array>} Promise resolving to the list of AR records
 */
export const getAccountsReceivable = async (customerId = null) => {
  try {
    const orgId = getOrgId();
    let url = `/orders/accounts-receivable/${orgId}`;
    
    if (customerId) {
      url += `?customer_id=${customerId}`;
    }
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching accounts receivable:', error);
    throw error;
  }
};

/**
 * Get accounts payable for a specific supplier or all suppliers
 * @param {number} supplierId - Optional supplier ID to filter by
 * @returns {Promise<Array>} Promise resolving to the list of AP records
 */
export const getAccountsPayable = async (supplierId = null) => {
  try {
    const orgId = getOrgId();
    let url = `/orders/accounts-payable/${orgId}`;
    
    if (supplierId) {
      url += `?supplier_id=${supplierId}`;
    }
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching accounts payable:', error);
    throw error;
  }
};

/**
 * Mark an AR payment as completed
 * @param {number} orderId - The order ID
 * @param {number} [paidAmount] - Optional paid amount for partial payments
 * @returns {Promise<Object>} Promise resolving to the updated order
 */
export const markARPaymentComplete = async (orderId, paidAmount) => {
  try {
    const payload = {
      completed: paidAmount !== undefined ? false : true, // If paidAmount is provided, we'll let the backend determine completion status
      completion_date: new Date().toISOString()
    };
    
    if (paidAmount !== undefined) {
      payload.paid_amount = paidAmount;
    }
    
    const response = await apiClient.patch(`/orders/${orderId}/ar-complete`, payload);
    return response.data;
  } catch (error) {
    console.error('Error marking AR payment as complete:', error);
    throw error;
  }
};

/**
 * Mark an AP payment as completed
 * @param {number} orderId - The order ID
 * @param {number} [paidAmount] - Optional paid amount for partial payments
 * @returns {Promise<Object>} Promise resolving to the updated order
 */
export const markAPPaymentComplete = async (orderId, paidAmount) => {
  try {
    const payload = {
      completed: paidAmount !== undefined ? false : true, // If paidAmount is provided, we'll let the backend determine completion status
      completion_date: new Date().toISOString()
    };
    
    if (paidAmount !== undefined) {
      payload.paid_amount = paidAmount;
    }
    
    const response = await apiClient.patch(`/orders/${orderId}/ap-complete`, payload);
    return response.data;
  } catch (error) {
    console.error('Error marking AP payment as complete:', error);
    throw error;
  }
};

export const getEmiInstallments = async (orgId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.customerId) params.append('customer_id', filters.customerId);
    if (filters.supplierId) params.append('supplier_id', filters.supplierId);
    if (filters.orderType) params.append('order_type', filters.orderType);
    
    const response = await apiClient.get(`/orders/emi-installments/${orgId}?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching EMI installments:', error);
    throw error;
  }
};

export const updateInstallmentStatus = async (installmentId, statusData) => {
  try {
    const response = await apiClient.patch(`/orders/emi-installments/${installmentId}/status`, statusData);
    return response.data;
  } catch (error) {
    console.error('Error updating installment status:', error);
    throw error;
  }
};

export const markInstallmentMissed = async (installmentId) => {
  try {
    const response = await apiClient.patch(`/orders/emi-installments/${installmentId}/status`, {
      status: 'missed',
    });
    return response.data;
  } catch (error) {
    console.error('Error marking installment as missed:', error);
    throw error;
  }
};

export const getOverdueInstallments = async (orgId) => {
  try {
    const response = await apiClient.get(`/orders/overdue-installments/${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching overdue installments:', error);
    throw error;
  }
};

/**
 * Create a return order for a parent order
 * @param {number} parentOrderId - The ID of the original order
 * @param {Object} returnData - The return data including returned items and reason
 * @returns {Promise<Object>} Promise resolving to the created return order
 */
export const createReturn = async (parentOrderId, returnData) => {
  try {
    const orgId = getOrgId();
    const formattedData = {
      ...returnData,
      org_id: orgId,
    };
    
    const response = await apiClient.post(`/orders/${parentOrderId}/return`, formattedData);
    return response.data;
  } catch (error) {
    console.error('Error creating return:', error);
    throw error;
  }
};

/**
 * Create an exchange order for a parent order
 * @param {number} parentOrderId - The ID of the original order
 * @param {Object} exchangeData - The exchange data including returned and exchanged items
 * @returns {Promise<Object>} Promise resolving to the created exchange order
 */
export const createExchange = async (parentOrderId, exchangeData) => {
  try {
    const orgId = getOrgId();
    const formattedData = {
      ...exchangeData,
      org_id: orgId,
    };
    
    const response = await apiClient.post(`/orders/${parentOrderId}/exchange`, formattedData);
    return response.data;
  } catch (error) {
    console.error('Error creating exchange:', error);
    throw error;
  }
};

/**
 * Get return and exchange history for an order
 * @param {number} orderId - The ID of the order
 * @returns {Promise<Array>} Promise resolving to the list of return/exchange orders
 */
export const getReturnHistory = async (orderId) => {
  try {
    const response = await apiClient.get(`/orders/${orderId}/return-history`);
    return response.data;
  } catch (error) {
    console.error('Error fetching return history:', error);
    throw error;
  }
};

/**
 * Toggle the payment status of an EMI installment
 * @param {number} installmentId - The installment ID
 * @returns {Promise<Object>} Promise resolving to the updated installment
 */
export const toggleInstallmentPaymentStatus = async (installmentId) => {
  try {
    const response = await apiClient.patch(`/orders/emi-installments/${installmentId}/toggle-payment`);
    return response.data;
  } catch (error) {
    console.error('Error toggling installment payment status:', error);
    throw error;
  }
};

/**
 * Get the payment history for an order
 * @param {number} orderId - The order ID
 * @returns {Promise<Array>} Promise resolving to the payment history
 */
export const getPaymentHistory = async (orderId) => {
  try {
    const response = await apiClient.get(`/orders/${orderId}/payment-history`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment history:', error);
    throw error;
  }
};

/**
 * Fetch organization payment history with pagination support.
 * @param {Number} orgId - The organization ID.
 * @param {Object} options - Pagination and filtering options.
 * @returns {Promise<Object>} Promise resolving to paginated payment history data.
 */
export const getOrganizationPaymentHistory = async (options = {}) => {
  try {
    const orgId = getOrgId();
    const params = new URLSearchParams();
    
    // Add pagination parameters if provided
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.search) params.append('search', options.search);
    if (options.payment_type) params.append('payment_type', options.payment_type);
    // Default to sorting by payment date in descending order if not specified
    params.append('sortBy', options.sortBy || 'ph.payment_date');
    params.append('sortOrder', options.sortOrder || 'DESC');
    
    const queryString = params.toString();
    const url = `/orders/payment-history/organization/${orgId}${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching organization payment history:', error);
    throw error;
  }
};

export const getCompletedAccountsReceivable = async (options = {}) => {
  try {
    const orgId = getOrgId();
    const params = new URLSearchParams();
    
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.customer_id) params.append('customer_id', options.customer_id);
    
    const queryString = params.toString();
    const url = `/orders/accounts-receivable/${orgId}/completed${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching completed accounts receivable:', error);
    throw error;
  }
};

export const getCompletedAccountsPayable = async (options = {}) => {
  try {
    const orgId = getOrgId();
    const params = new URLSearchParams();
    
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.supplier_id) params.append('supplier_id', options.supplier_id);
    
    const queryString = params.toString();
    const url = `/orders/accounts-payable/${orgId}/completed${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching completed accounts payable:', error);
    throw error;
  }
};
